import axios from 'axios'

import { getMsalInstance } from 'authConfig'
import { getUserToken } from './utility/localStorageFunctions'
import {
  getDefaultQueryString,
  getLimitQueryString,
} from './utility/queryFunctions'

const BASE_URL = `${process.env.REACT_APP_BASE_API_URL}/users-api/users`

export async function getUser() {
  const msalInstance = await getMsalInstance()
  const account = msalInstance.getActiveAccount()
  if (!account) {
    throw Error(
      'No active account! Verify a user has been signed in and setActiveAccount has been called.',
    )
  }

  const token = await getUserToken('users')

  const headers = new Headers()
  const bearer = `Bearer ${token}`

  headers.append('Authorization', bearer)

  const options = {
    method: 'GET',
    headers: headers,
  }

  return fetch(
    BASE_URL.concat(`/${account.idTokenClaims?.['extension_Jyrney_userId']}`),
    options,
  ).then((response) => response.json())
}

async function getUserById(userId: any) {
  const token = await getUserToken('users')
  const response = await axios({
    method: 'get',
    url: BASE_URL.concat(`/${userId}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

async function getUsersByClientId(clientId: any) {
  const token = await getUserToken('users')
  const response = await axios({
    method: 'get',
    url: BASE_URL.concat(getLimitQueryString('clientId', clientId)),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

async function getUsersBySupplierId(clientId: any) {
  const token = await getUserToken('users')
  const response = await axios({
    method: 'get',
    url: BASE_URL.concat(getDefaultQueryString('supplierId', clientId)),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

function mapDelegatesForPatch(delegates: any[] = []) {
  return delegates.map((delegate: any) => {
    const normalizedDelegate: any = {
      permissionTypeName: delegate.permissionTypeName,
      delegateId: delegate.delegate?.delegateId ?? delegate.delegateId,
    }

    const startDateTime =
      delegate.startDateTime !== undefined
        ? delegate.startDateTime
        : delegate.delegate?.startDateTime
    const endDateTime =
      delegate.endDateTime !== undefined
        ? delegate.endDateTime
        : delegate.delegate?.endDateTime

    if (startDateTime !== undefined) {
      normalizedDelegate.startDateTime = startDateTime
    }
    if (endDateTime !== undefined) {
      normalizedDelegate.endDateTime = endDateTime
    }

    return normalizedDelegate
  })
}

async function updateUser(updatedUser: any, userId: any) {
  const token = await getUserToken('users')
  const payload = { ...updatedUser }
  if (Object.prototype.hasOwnProperty.call(payload, 'delegates')) {
    payload.delegates = mapDelegatesForPatch(payload.delegates ?? [])
  }

  const response = await axios({
    method: 'patch',
    url: BASE_URL.concat(`/${userId}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: payload,
    },
  })
  return response
}

async function changePassword(email: string, password: string) {
  const token = await getUserToken('users')
  const response = await axios({
    method: 'post',
    url: BASE_URL.concat('/changePassword'),
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        email: email,
        password: password,
        confirmPassword: password,
      },
    },
  })
  return response
}

async function registerUser(userToRegister: any) {
  const token = await getUserToken('users')
  const response = await axios({
    method: 'post',
    url: BASE_URL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        ...userToRegister,
      },
    },
  })
  return response
}

async function deleteUser(userid: any) {
  const token = await getUserToken('users')
  const response = await axios({
    method: 'delete',
    url: BASE_URL.concat(`/${userid}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

async function getUserDashboards(userid: any) {
  const token = await getUserToken('users')
  const response = await axios({
    method: 'get',
    url: BASE_URL.concat(`/${userid}/dashboards`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

async function resendEmailConfirmation(userid: any) {
  const token = await getUserToken('users')
  const response = await axios({
    method: 'post',
    url: BASE_URL.concat(`/${userid}/resendCompleteRegistration`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

export const usersAPI = {
  getUser,
  getUserById,
  getUsersByClientId,
  updateUser,
  changePassword,
  registerUser,
  deleteUser,
  getUserDashboards,
  getUsersBySupplierId,
  resendEmailConfirmation,
}
